import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaTrash, FaEye, FaEyeSlash, FaReply } from "react-icons/fa";
import { contactService, ContactMessage } from "../../services/firebase";
import "./Contact.css";

const AdminContact: React.FC = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const data = await contactService.getAll();
      setMessages(data);
    } catch (error) {}
  };

  const handleViewMessage = (message: ContactMessage) => {
    setSelectedMessage(message);
    setIsModalOpen(true);

    // סמן כנקרא אם לא נקרא עדיין
    if (!message.read && message.id) {
      contactService.markAsRead(message.id);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === message.id ? { ...msg, read: true } : msg,
        ),
      );
    }
  };

  const handleReplyMessage = (message: ContactMessage) => {
    const subject = encodeURIComponent("sullabread");
    const body = encodeURIComponent(
      `\n\n--- Original Message ---\n${message.message}`,
    );
    window.location.href = `mailto:${message.email}?subject=${subject}&body=${body}`;
  };

  const handleDeleteMessage = async (id: string) => {
    if (window.confirm(t("admin.confirmDelete"))) {
      try {
        await contactService.delete(id);
        fetchMessages();
        if (selectedMessage?.id === id) {
          setIsModalOpen(false);
          setSelectedMessage(null);
        }
      } catch (error) {}
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "תאריך לא ידוע";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("he-IL", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="contact-page-container">
      <div className="contact-header">
        <h1 className="contact-title">{t("admin.contactMessages")}</h1>
      </div>

      <div className="messages-grid">
        {messages.length === 0 ? (
          <div className="empty-state">אין הודעות קשר</div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`message-card ${message.read ? "read" : "unread"}`}
            >
              <div className="message-header">
                <div className="message-info">
                  <h3 className="message-name">{message.name}</h3>
                  <div className="message-email">{message.email}</div>
                  <div className="message-date">
                    {formatDate(message.createdAt)}
                  </div>
                </div>
                <div className="message-actions">
                  <button
                    className={`action-button read ${
                      message.read ? "read-message" : ""
                    }`}
                    onClick={() => handleViewMessage(message)}
                    title={message.read ? "צפייה בהודעה" : "סמן כנקרא"}
                  >
                    {message.read ? <FaEye /> : <FaEyeSlash />}
                  </button>
                  <button
                    className="action-button reply"
                    onClick={() => handleReplyMessage(message)}
                    title="השב להודעה"
                    aria-label="השב להודעה"
                  >
                    <FaReply />
                  </button>
                  <button
                    className="action-button delete"
                    onClick={() =>
                      message.id && handleDeleteMessage(message.id)
                    }
                    title="מחק הודעה"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
              <div className="message-content">
                {message.message.length > 200
                  ? `${message.message.substring(0, 200)}...`
                  : message.message}
              </div>
            </div>
          ))
        )}
      </div>

      {isModalOpen && selectedMessage && (
        <div className="contact-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">{selectedMessage.name}</h2>
              <div className="modal-meta">
                {selectedMessage.email} •{" "}
                {formatDate(selectedMessage.createdAt)}
              </div>
            </div>
            <div className="message-content">{selectedMessage.message}</div>
            <div className="modal-actions">
              <button
                className="contact-button secondary"
                onClick={() => setIsModalOpen(false)}
              >
                {t("admin.close")}
              </button>
              <button
                className="contact-button primary"
                onClick={() => handleReplyMessage(selectedMessage)}
              >
                השב
              </button>
              {selectedMessage.id && (
                <button
                  className="contact-button primary"
                  onClick={() => handleDeleteMessage(selectedMessage.id!)}
                >
                  {t("admin.delete")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContact;
